import { Loader2 } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const Table = ({
  title,
  headers,
  data,
  buttonData,
  mainHeader,
  sortConfig = {},
  maxHeight,
  searchBar,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [sortedData, setSortedData] = useState(data);
  const [sortState, setSortState] = useState({
    column: null,
    direction: null,
  });

  useEffect(() => {
    setSortedData(data);
  }, [data]);

  const getSortIcon = (header) => {
    if (!sortConfig[header]) return null;

    if (sortState.column !== header) {
      return (
        <svg
          className="ml-2 h-4 w-4 inline-block text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return sortState.direction === "asc" ? (
      <svg
        className="ml-2 h-4 w-4 inline-block text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="ml-2 h-4 w-4 inline-block text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handleSort = (header) => {
    if (!sortConfig[header]) return;

    const newDirection =
      sortState.column === header && sortState.direction === "asc"
        ? "desc"
        : "asc";
    setSortState({ column: header, direction: newDirection });

    const sorted = React.Children.toArray(data).sort((a, b) => {
      const aValue = sortConfig[header](a);
      const bValue = sortConfig[header](b);
      return newDirection === "asc"
        ? typeof aValue === "string"
          ? aValue.localeCompare(bValue)
          : aValue - bValue
        : typeof aValue === "string"
          ? bValue.localeCompare(aValue)
          : bValue - aValue;
    });

    setSortedData(sorted);
  };

  return (
    <div className="h-full flex flex-col bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden w-full">
      {(mainHeader || title || searchBar || buttonData) && (
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 sm:py-5 gap-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1 min-w-0 w-full">
            {(mainHeader || title) && (
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">
                {mainHeader ? mainHeader : title}
              </h2>
            )}
            {searchBar && (
              <div className="w-full sm:flex-1 min-w-0">{searchBar}</div>
            )}
          </div>
          {buttonData && (
            <div className="flex-shrink-0 w-full sm:w-auto">{buttonData}</div>
          )}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-hidden bg-white w-full">
        <div
          className="h-full overflow-x-auto overflow-y-auto w-full"
          style={maxHeight ? { maxHeight, overflowY: "auto" } : {}}
        >
          <div className="min-w-full align-middle w-full">
            <table className="min-w-full w-full">
              <thead className="bg-gray-50/50 sticky top-0 z-10 border-b border-gray-200">
                <tr>
                  {headers
                    ?.filter(
                      (header) =>
                        ![
                          t("common.edit"),
                          t("common.delete"),
                          t("common.action"),
                        ].includes(header)
                    )
                    .map((header, index) => (
                      <th
                        key={index}
                        scope="col"
                        className={`px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap ${header === t("common.action")
                            ? "text-right"
                            : "text-left"
                          } 
                          text-xs font-semibold text-gray-600 uppercase tracking-wider
                          ${sortConfig[header]
                            ? "cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                            : ""
                          }`}
                        onClick={() => handleSort(header)}
                      >
                        <span className="flex items-center gap-2">
                          {header}
                          {getSortIcon(header)}
                        </span>
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={headers.length} className="text-center py-8">
                      <Loader2 className="h-10 w-10 mx-auto animate-spin text-gray-400" />
                    </td>
                  </tr>
                ) : (
                  sortedData
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;
